// AgentHook (AIAgent_AgentHooks junction table) sub-entity client — restores the "Hooks" Agent
// tab dropped in the 2026-08-19/20 AgentEditor.tsx refactor. There was no standalone old
// frontend file for this one (it lived as an inline section of the deleted AgentEditor.tsx,
// driven by a generic `AssignmentModal` with `entityType="hook"`) — this client and its panel
// are a fresh, self-contained build against the real live backend
// (BizFirst.Ai.AiAgent.Api.Base's BaseAgentHookController), not an adaptation of prior code.
//
// Confirmed by reading BaseAgentHookController.cs directly: there is no link/unlink API at all —
// it's the same generic Create/Update/Delete/GetAll/GetById/GetByAgent/GetActive/Activate/
// Deactivate/Validate shape every other sub-entity controller here has, because
// AIAgent_AgentHooks (BizFirst.Ai.AiAgent.Domain.Entities.AgentHook) is a plain, directly
// CRUD-able junction row (AgentID/HookID/IsActive + ScopeMode/OwnerScope/ConfigJson memory-hook
// overrides — see types.ts's AgentHookDTO comment). "Link" is therefore Create on that row,
// "unlink" is Delete — there's nothing else to call. List route is `by-agent` (confirmed;
// **not** `by-agent-id` as the task's briefing initially flagged as a possible naming trap).
//
// getAvailableHooks lists Hook *definitions* (AIExt_Hooks, a different entity/controller —
// BizFirst.Ai.AIExtension.Api.Base's BaseHookController @ /api/v1/ai-extension/hooks) via its
// confirmed `active` route, powering the "Add Hook" picker dropdown (pick a hook to link, by
// HookID). This is the same `.../active` GetActive convention every GetActiveXxxAsync action in
// this codebase follows (BaseHookController.GetActive -> HookService.GetActiveHooksAsync).

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { AgentHookDTO, AgentHookFormValues, HookDTO } from '../types';

export class AgentHookAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  // --- AgentHook junction rows (per-agent links) ---

  async getHooksByAgent(agentId: number): Promise<AdminApiResult<AgentHookDTO[]>> {
    const result = await this.postQuery<AgentHookDTO[]>('/api/v1/ai-agent/agent-hooks/by-agent', {
      AgentID: { ID: agentId },
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  /** "Link" a hook to this agent — plain Create on the junction row, per BaseAgentHookController's
   *  own shape (no dedicated link endpoint exists). */
  async linkHook(agentId: number, hook: AgentHookFormValues): Promise<AdminApiResult<AgentHookDTO>> {
    return this.post<AgentHookDTO>('/api/v1/ai-agent/agent-hooks', { AgentID: agentId, ...hook });
  }

  async updateHookLink(agentHookId: number, hook: Partial<AgentHookFormValues>): Promise<AdminApiResult<AgentHookDTO>> {
    return this.put<AgentHookDTO>(`/api/v1/ai-agent/agent-hooks/${agentHookId}`, {
      AgentHookID: agentHookId,
      ...hook,
    });
  }

  /** "Unlink" — plain Delete on the junction row (no dedicated unlink endpoint exists). */
  async unlinkHook(agentHookId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-agent/agent-hooks/${agentHookId}`, { id: agentHookId });
  }

  // --- Hook definitions (AIExt_Hooks) — read-only lookup source for the "Add Hook" picker ---

  private availableHooksCache: HookDTO[] | null = null;

  async getAvailableHooks(): Promise<AdminApiResult<HookDTO[]>> {
    if (this.availableHooksCache) return { success: true, data: this.availableHooksCache };
    const result = await this.postQuery<HookDTO[]>('/api/v1/ai-extension/hooks/active', {
      PageInfo: { PageNumber: 1, PageSize: 500 },
    });
    if (!result.success || !Array.isArray(result.data)) return { ...result, data: [] };
    this.availableHooksCache = result.data;
    return { success: true, data: result.data };
  }
}
